import React from 'react';

type ExecuteProps = {
  specActions: { execute: (request: unknown) => unknown };
  operation: unknown;
  path: string;
  method: string;
};
export function blockSwaggerRequest(): never {
  throw new Error('API execution is disabled. Copy the request into Postman.');
}

function isMissing(value: any): boolean {
  return (
    value == null ||
    (typeof value === 'string' && value.trim() === '') ||
    (Array.isArray(value) && value.length === 0)
  );
}

function placeholderValue(schema: any, name: string): any {
  const marker = `REPLACE_${name.replace(/[^a-z0-9]+/gi, '_').toUpperCase()}`;
  if (schema?.type === 'file' || schema?.format === 'binary') {
    return new File([], marker);
  }
  if (schema?.type === 'array') return [marker];
  if (schema?.type === 'object' || schema?.properties || schema?.$ref) {
    return { REPLACE_WITH_VALID_VALUE: marker };
  }
  return marker;
}

function addRequiredPlaceholders(
  request: any,
  operation: any,
  placeholders: string[],
) {
  const parameters = new Map<string, any>();
  for (const parameter of [
    ...(request.spec?.paths?.[request.pathName]?.parameters || []),
    ...(operation.parameters || []),
  ]) {
    parameters.set(`${parameter.in}.${parameter.name}`, parameter);
  }
  for (const [qualified, parameter] of parameters) {
    if (!parameter.required && parameter.in !== 'path') continue;
    const key =
      request.parameters[parameter.name] !== undefined
        ? parameter.name
        : qualified;
    const value = request.parameters[key];
    if (!isMissing(value) || (parameter.allowEmptyValue && value === ''))
      continue;
    const schema = parameter.schema || parameter;
    const replacement = placeholderValue(schema, parameter.name);
    request.parameters[key] =
      parameter.in === 'body' && !(replacement instanceof File)
        ? JSON.stringify(replacement)
        : replacement;
    placeholders.push(qualified);
  }

  const body = operation.requestBody;
  if (!body) return;
  const mediaType =
    request.requestContentType || Object.keys(body.content || {})[0];
  const schema = body.content?.[mediaType]?.schema;
  const form =
    mediaType?.includes('form-data') || mediaType?.includes('form-urlencoded');
  if (form) {
    const values = { ...request.requestBody };
    if (!body.required && Object.keys(values).length === 0) return;
    for (const name of schema?.required || []) {
      if (isMissing(values[name])) {
        values[name] = placeholderValue(schema.properties?.[name], name);
        placeholders.push(`body.${name}`);
      }
    }
    if (body.required && Object.keys(values).length === 0) {
      values.REPLACE_FIELD = 'REPLACE_BODY';
      placeholders.push('body');
    }
    if (Object.keys(values).length) request.requestBody = values;
  } else if (body.required && isMissing(request.requestBody)) {
    const replacement = placeholderValue(schema || { type: 'object' }, 'BODY');
    request.requestBody = mediaType?.includes('json')
      ? JSON.stringify(replacement)
      : replacement;
    placeholders.push('body');
  }
}

// Swagger exposes its request builder and Immutable state through the plugin system.
export function buildPostmanRequest(
  system: any,
  input: any,
  placeholders: string[] = [],
) {
  const { specSelectors, oas3Selectors, fn } = system;
  const request = { ...input, parameters: { ...input.parameters } };
  const operation = input.operation.toJS();
  request.contextUrl = specSelectors.url() || window.location.href;
  request.operationId =
    operation.operationId ||
    fn.opId(operation, request.pathName, request.method);

  input.operation.get('parameters')?.forEach((parameter: any) => {
    const name = parameter.get('name');
    const location = parameter.get('in');
    if (
      parameter.get('allowEmptyValue') &&
      specSelectors.parameterInclusionSettingFor(
        [request.pathName, request.method],
        name,
        location,
      ) &&
      request.parameters[`${location}.${name}`] == null
    ) {
      request.parameters[`${location}.${name}`] = '';
    }
  });

  if (specSelectors.isOAS3()) {
    const namespace = `${request.pathName}:${request.method}`;
    request.server =
      oas3Selectors.selectedServer(namespace) || oas3Selectors.selectedServer();
    const local = oas3Selectors
      .serverVariables({ server: request.server, namespace })
      .toJS();
    request.serverVariables = Object.keys(local).length
      ? local
      : oas3Selectors.serverVariables({ server: request.server }).toJS();
    request.requestContentType = oas3Selectors.requestContentType(
      request.pathName,
      request.method,
    );
    request.responseContentType =
      oas3Selectors.responseContentType(request.pathName, request.method) ||
      '*/*';
    const body = oas3Selectors.requestBodyValue(
      request.pathName,
      request.method,
    );
    const included = oas3Selectors.requestBodyInclusionSetting(
      request.pathName,
      request.method,
    );
    request.requestBody = body?.toJS
      ? body
          .map((value: any) => (value?.get ? value.get('value') : value))
          .filter(
            (value: any, key: string) => !isMissing(value) || included.get(key),
          )
          .toJS()
      : body;
  }

  // Fill only the export copy; required flags and the editable Swagger state stay intact.
  addRequiredPlaceholders(request, operation, placeholders);

  // buildRequest serializes parameters, body and user-supplied API authorization.
  // It does not execute HTTP or use Backstage's authenticated FetchApi.
  const built = fn.buildRequest(request);
  system.specActions.setRequest(request.pathName, request.method, built);
  return fn.requestSnippetGenerator_curl_bash(
    specSelectors.requestFor(request.pathName, request.method),
  );
}

export function createPostmanPlugin(
  onCopy: (curl: string, operation: string, placeholders: string[]) => void,
  onError: () => void,
) {
  return () => ({
    components: {
      TryItOutButton: () => null,
      onlineValidatorBadge: () => null,
    },
    wrapComponents: {
      execute: (_original: unknown) =>
        function CopyCurlButton({
          specActions,
          operation,
          path,
          method,
        }: ExecuteProps) {
          return (
            <button
              type="button"
              className="btn execute opblock-control__btn"
              onClick={() => specActions.execute({ operation, path, method })}
            >
              Copiar cURL
            </button>
          );
        },
    },
    statePlugins: {
      spec: {
        wrapActions: {
          // Wrapped actions are already bound; returning another thunk does not run it.
          executeRequest:
            (_original: unknown, system: any) => (request: any) => {
              try {
                const placeholders: string[] = [];
                const curl = buildPostmanRequest(system, request, placeholders);
                onCopy(
                  curl,
                  `${request.method.toUpperCase()} ${request.pathName}`,
                  placeholders,
                );
              } catch {
                onError();
              }
            },
        },
      },
    },
    // Defense in depth: no execution path in this widget may send an API request.
    fn: { execute: blockSwaggerRequest, fetch: blockSwaggerRequest },
  });
}
